
import React, { useState } from 'react';
import SearchFilter from '@/components/SearchFilter';
import { Search } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import MobileMenu from '@/components/MobileMenu';
import AddBusinessDropdown from '@/components/AddBusinessDropdown';
import { Portfolio } from '@/schemas/portfolio'; // Updated import
import { FilterOptions } from '@/components/FilterModal';
import { cn } from '@/lib/utils';
import GridToggle from './GridToggle';
import { LayoutMode } from '@/hooks/usePortfolioState';
import { Button } from '@/components/ui/button';
import InlineFilterSection from './InlineFilterSection';
import FilterButton from '@/components/ui/FilterButton';
import ComingSoonModal from '../ComingSoonModal';
import { FEATURE_FLAGS } from '@/config/featureFlag';

interface HeaderSectionProps {
  searchTerm: string;
  filters: FilterOptions;
  layoutMode: LayoutMode;
  setLayoutMode: (mode: LayoutMode) => void;
  showMobileSearch: boolean;
  setShowMobileSearch: (show: boolean) => void;
  showFilterButton: boolean;
  setShowFilterButton: (show: boolean) => void;
  showFilterOptions: boolean;
  setShowFilterOptions: (show: boolean) => void;
  onSearch: (term: string) => void;
  onFilterChange: (filters: FilterOptions) => void;
  onFilterApply: () => void;
  onAddNewPortfolio: () => void;
  onSelectPortfolio: (portfolioId: string) => void;
  onShowMobileAddBusinessModal: () => void;
  allPortfolios: Portfolio[];
  filteredPortfolios: Portfolio[];
  toggleFilterOptions: () => void;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({
  searchTerm,
  filters,
  layoutMode,
  setLayoutMode,
  showMobileSearch,
  setShowMobileSearch,
  showFilterButton,
  setShowFilterButton,
  showFilterOptions,
  setShowFilterOptions,
  onSearch,
  onFilterChange,
  onFilterApply,
  onAddNewPortfolio,
  onSelectPortfolio,
  onShowMobileAddBusinessModal,
  allPortfolios,
  filteredPortfolios,
  toggleFilterOptions
}) => {
  const isMobile = useIsMobile();
  const [showDesktopSearch, setShowDesktopSearch] = useState(false);
  const [portfolioSearch, setPortfolioSearch] = useState('');
  const [industrySearch, setIndustrySearch] = useState('');
  const [comingSoonModalOpen, setComingSoonModalOpen] = useState(false);

  const hasActiveFilters = filters.status.length > 0 || 
                          filters.portfolios.length > 0 || 
                          filters.industries.length > 0;

  const toggleDesktopSearch = () => {
    setShowDesktopSearch(!showDesktopSearch);
    if (showDesktopSearch) {
      onSearch(''); // Clear search when toggling off
    }
  };

  const handleResetFilters = () => {
    onFilterChange({
      status: [],
      portfolios: [],
      industries: []
    });
    setPortfolioSearch('');
    setIndustrySearch('');
  };

  return (
    <div 
      className="flex flex-col gap-2 header-section" 
      style={{ 
        boxShadow: 'none',
        transition: 'all 0.2s ease-in-out',
        marginTop: '0px',
        marginBottom: showFilterOptions ? (isMobile ? '14px' : '4px') : '0px',
        paddingTop: '1.5rem', 
        paddingBottom: showFilterOptions ? (isMobile ? '8px' : '6px') : '1.5rem',
        position: 'sticky',
        top: '57px', // Updated to 57px
        zIndex: 30,
      }}
    >
      <div className="flex flex-row justify-between items-center relative">
        <div className={cn("flex items-center gap-2", isMobile && "pl-2")}>
          <h1 className="text-2xl font-semibold text-gray-900">My Businesses</h1>
          
          {!isMobile && (
            <div className="flex items-center gap-2 ml-2">
              <GridToggle 
                layout={layoutMode}
                onChange={setLayoutMode}
              />
              
              {FEATURE_FLAGS.PORTFOLIO_FILTERS && (
                <>
                  <Button 
                    variant={showDesktopSearch ? "outline" : "ghost"}
                    size="icon" 
                    className="h-8 w-8 rounded-full"
                    onClick={toggleDesktopSearch}
                  >
                    <Search size={20} className={showDesktopSearch ? "text-brand-green" : "text-gray-600"} />
                  </Button>
                  
                  <FilterButton 
                    isActive={showFilterOptions}
                    hasFilters={hasActiveFilters}
                    onClick={toggleFilterOptions}
                  />
                </>
              )}
            </div>
          )}
        </div>
        
        {isMobile && (
          <MobileMenu 
            onAddNewPortfolio={onAddNewPortfolio}
            onSearch={onSearch}
            currentSearchTerm={searchTerm}
            setShowMobileSearch={setShowMobileSearch}
            showMobileSearch={showMobileSearch}
            setShowFilterButton={setShowFilterButton}
            showFilterButton={showFilterButton}
            toggleFilterButton={toggleFilterOptions}
            showFilterOptions={showFilterOptions}
            hasActiveFilters={hasActiveFilters}
            onShowMobileAddBusinessModal={onShowMobileAddBusinessModal}
          />
        )}
        
        {!isMobile && (
          <div className="flex items-center gap-4">
            {showDesktopSearch && (
              <div className="w-64 mr-2 animate-in fade-in slide-in-from-right-5 duration-300">
                <SearchFilter 
                  onSearch={onSearch}
                  defaultValue={searchTerm}
                />
              </div>
            )}
            
            <AddBusinessDropdown 
              portfolios={allPortfolios}
              onCreateNewPortfolio={() => setComingSoonModalOpen(true)}
              onSelectPortfolio={() => setComingSoonModalOpen(true)}
            />
          </div>
        )}
      </div>
      
      {isMobile && showMobileSearch && (
        <div className="w-full mb-2">
          <SearchFilter 
            onSearch={onSearch}
            defaultValue={searchTerm}
          />
        </div>
      )}
      <ComingSoonModal 
        open={comingSoonModalOpen} 
        onOpenChange={setComingSoonModalOpen}
        description='To Create Business or Portfolio please contact us.' 
      />

      {showFilterOptions && (
        <InlineFilterSection
          filters={filters}
          onFilterChange={onFilterChange}
          onReset={handleResetFilters}
          portfolioSearch={portfolioSearch}
          setPortfolioSearch={setPortfolioSearch}
          industrySearch={industrySearch}
          setIndustrySearch={setIndustrySearch}
          filteredPortfolios={filteredPortfolios}
        />
      )}
    </div>
  );
};

export default HeaderSection;
